import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { changePassword, previewCountryChange, changeCountry, deleteAccount, getEmailPreferences, updateEmailPreferences } from '../utils/api';
import { toast } from 'sonner';
import { User, Lock, Globe, LogOut, AlertTriangle, CheckCircle, Play, Trash2, X, Mail, Users, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import OnboardingFlow from '../components/OnboardingFlow';
import ShareMyCBCModal from '../components/ShareMyCBCModal';

// Shared country configuration
const BASE_COUNTRIES = [
  { code: 'AU', name: 'Australia', flag: '🇦🇺', units: 'SI (g/L)' },
  { code: 'NZ', name: 'New Zealand', flag: '🇳🇿', units: 'SI (g/L)' },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧', units: 'SI (g/L)' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦', units: 'SI (g/L)' },
  { code: 'US', name: 'United States', flag: '🇺🇸', units: 'US (g/dL)' },
  { code: 'OTHER', name: 'Other / Not sure', flag: '🌍', units: 'SI (g/L)' }
];

// Helper to reorder countries with selected country first
const getOrderedCountries = (selectedCode) => {
  if (!selectedCode) return BASE_COUNTRIES;
  const selected = BASE_COUNTRIES.find(c => c.code === selectedCode);
  if (!selected) return BASE_COUNTRIES;
  return [selected, ...BASE_COUNTRIES.filter(c => c.code !== selectedCode)];
};

const getCountryName = (code) => {
  const country = BASE_COUNTRIES.find(c => c.code === code);
  return country ? country.name : code;
};

const getCountryFlag = (code) => {
  const country = BASE_COUNTRIES.find(c => c.code === code);
  return country ? country.flag : '🌍';
};

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, logout, updateUser } = useAuth();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  
  const [selectedCountry, setSelectedCountry] = useState(user?.country || 'AU');
  const [showCountryDialog, setShowCountryDialog] = useState(false);
  const [conversionPreview, setConversionPreview] = useState(null);
  const [countryLoading, setCountryLoading] = useState(false);

  // Account deletion state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const CONFIRM_PHRASE = 'DELETE MY ACCOUNT';

  // Email preferences state
  const [emailFrequency, setEmailFrequency] = useState('instant');
  const [emailLoading, setEmailLoading] = useState(false);

  // Share MyCBC modal state
  const [showShareModal, setShowShareModal] = useState(false);

  // Fetch email preferences on mount
  useEffect(() => {
    const fetchEmailPrefs = async () => {
      try {
        const prefs = await getEmailPreferences();
        setEmailFrequency(prefs.frequency || 'instant');
      } catch (error) {
        console.error('Failed to load email preferences');
      }
    };
    fetchEmailPrefs();
  }, []);

  const handleEmailPreferenceChange = async (frequency) => {
    setEmailLoading(true);
    try {
      await updateEmailPreferences(frequency);
      setEmailFrequency(frequency);
      toast.success('Email preferences updated');
    } catch (error) {
      toast.error('Failed to update email preferences');
    } finally {
      setEmailLoading(false);
    }
  };

  // Password validation
  const validatePassword = (password) => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/[A-Za-z]/.test(password)) {
      return 'Password must contain letters';
    }
    if (!/\d/.test(password)) {
      return 'Password must contain numbers';
    }
    return null;
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      toast.error('Please fill in all password fields');
      return;
    }

    const passwordError = validatePassword(passwordData.newPassword);
    if (passwordError) {
      toast.error(passwordError);
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    setPasswordLoading(true);
    try {
      await changePassword(passwordData.currentPassword, passwordData.newPassword);
      toast.success('Password changed successfully');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleCountryPreview = async (newCountry) => {
    if (newCountry === user?.country) {
      toast.info('This is already your selected country');
      return;
    }

    // Update selectedCountry before opening modal
    setSelectedCountry(newCountry);
    
    setCountryLoading(true);
    try {
      const preview = await previewCountryChange(newCountry);
      setConversionPreview(preview);
      setShowCountryDialog(true);
    } catch (error) {
      // If preview fails, still allow the change with a fallback preview
      console.warn('Preview failed, using fallback:', error);
      setConversionPreview({
        needs_conversion: false,
        affected_count: 0,
        from_country: user?.country || 'AU',
        to_country: newCountry,
        from_unit: 'g/L',
        to_unit: 'g/L',
        message: 'Reference ranges will be updated to match your selected country.'
      });
      setShowCountryDialog(true);
    } finally {
      setCountryLoading(false);
    }
  };

  const handleCountryChangeConfirm = async () => {
    setCountryLoading(true);
    try {
      const response = await changeCountry(selectedCountry);
      
      // Update user context immediately with new country
      updateUser({ country: selectedCountry });
      
      setShowCountryDialog(false);
      setConversionPreview(null);
      
      // Show success message with info about what changed
      toast.success(`Country changed to ${getCountryName(selectedCountry)}. Haemoglobin units updated.`);
      
      // Reload after a short delay to refresh blood results with new units
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      toast.error('Failed to change country');
      console.error('Country change error:', error);
    } finally {
      setCountryLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    window.location.href = '/';
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== CONFIRM_PHRASE) {
      toast.error('Please type the confirmation phrase exactly as shown');
      return;
    }

    setDeleteLoading(true);
    try {
      await deleteAccount();
      // Clear local storage and redirect
      logout();
      toast.success('Your account has been permanently deleted');
      window.location.href = '/';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete account. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const openDeleteModal = () => {
    setDeleteConfirmText('');
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setDeleteConfirmText('');
    setShowDeleteModal(false);
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-2">Profile</h1>
        <p className="text-lg leading-relaxed text-slate-700">Manage your account settings</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Account Information */}
        <div className="bg-white rounded-2xl p-6 md:p-8 border border-slate-100 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-primary/10 rounded-xl">
              <User className="h-6 w-6 text-primary" />
            </div>
            <h2 className="text-2xl font-semibold text-slate-800">Account Information</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-600">Name</label>
              <p className="text-lg text-slate-900 mt-1">{user?.name}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-600">Email</label>
              <p className="text-lg text-slate-900 mt-1">{user?.email}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-600">Current Country</label>
              <p className="text-lg text-slate-900 mt-1">
                {getCountryName(user?.country)}
              </p>
            </div>
          </div>
        </div>

        {/* Change Password */}
        <div className="bg-white rounded-2xl p-6 md:p-8 border border-slate-100 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-primary/10 rounded-xl">
              <Lock className="h-6 w-6 text-primary" />
            </div>
            <h2 className="text-2xl font-semibold text-slate-800">Change Password</h2>
          </div>

          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Current Password
              </label>
              <input
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                className="w-full h-12 px-4 rounded-xl border border-slate-200 bg-white text-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                placeholder="Enter current password"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                New Password
              </label>
              <input
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                className="w-full h-12 px-4 rounded-xl border border-slate-200 bg-white text-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                placeholder="Enter new password"
              />
              <p className="text-xs text-slate-500 mt-1">
                Minimum 8 characters, must include letters and numbers
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Confirm New Password
              </label>
              <input
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                className="w-full h-12 px-4 rounded-xl border border-slate-200 bg-white text-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                placeholder="Confirm new password"
              />
            </div>

            <Button
              type="submit"
              disabled={passwordLoading}
              className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {passwordLoading ? 'Changing Password...' : 'Change Password'}
            </Button>
          </form>
        </div>
      </div>

      {/* Change Country */}
      <div className="bg-white rounded-2xl p-6 md:p-8 border border-slate-100 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-primary/10 rounded-xl">
            <Globe className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h2 className="text-2xl font-semibold text-slate-800">Country Settings</h2>
            <p className="text-sm text-slate-600 mt-1">
              Your country setting determines the units and reference ranges used for blood test results
            </p>
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
          <div className="flex gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-semibold mb-1">Important: Changing your country will affect your data</p>
              <ul className="list-disc list-inside space-y-1 text-amber-700">
                <li>Blood test reference ranges will be updated</li>
                <li>Existing haemoglobin values will be automatically converted to the new country&apos;s units</li>
                <li>Charts, alerts, and PDF exports will use the new units</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-3">Select Country</label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {getOrderedCountries(user?.country).map((country) => (
                <button
                  key={country.code}
                  type="button"
                  onClick={() => handleCountryPreview(country.code)}
                  disabled={countryLoading}
                  className={`p-3 rounded-xl border-2 transition-all ${
                    user?.country === country.code || (country.code === 'OTHER' && !BASE_COUNTRIES.find(c => c.code === user?.country))
                      ? 'border-primary bg-primary/5'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="text-left">
                    <p className="font-semibold text-slate-900 text-sm flex items-center gap-2">
                      <span>{country.flag}</span>
                      {country.name}
                    </p>
                    <p className="text-xs text-slate-600 mt-0.5">{country.units}</p>
                    {user?.country === country.code && (
                      <p className="text-xs text-primary mt-1 flex items-center gap-1">
                        <CheckCircle className="h-3 w-3" />
                        Current
                      </p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Email Notifications */}
      <div className="bg-white rounded-2xl p-6 md:p-8 border border-slate-100 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-teal-50 rounded-xl flex-shrink-0">
            <Mail className="h-6 w-6 text-teal-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-slate-800">Email Notifications</h2>
            <p className="text-sm text-slate-600">Choose how often you receive email updates</p>
          </div>
        </div>
        
        {/* 2x2 grid on all screens */}
        <div className="grid grid-cols-2 gap-3">
          {[
            { value: 'instant', label: 'Instant', desc: 'Same day (max 1 news email/day)' },
            { value: 'daily', label: 'Daily', desc: 'Daily digest at 12:30pm' },
            { value: 'weekly', label: 'Weekly', desc: 'Friday roundup' },
            { value: 'none', label: 'None', desc: 'In-app only' }
          ].map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => !emailLoading && handleEmailPreferenceChange(option.value)}
              disabled={emailLoading}
              className={`p-4 rounded-xl border-2 text-left transition-all ${
                emailFrequency === option.value
                  ? 'border-teal-500 bg-teal-50'
                  : 'border-slate-200 hover:border-slate-300'
              } ${emailLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                  emailFrequency === option.value ? 'border-teal-500' : 'border-slate-300'
                }`}>
                  {emailFrequency === option.value && (
                    <div className="w-2.5 h-2.5 rounded-full bg-teal-500" />
                  )}
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900">{option.label}</p>
                  <p className="text-xs text-slate-500">{option.desc}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Getting Started / Replay Onboarding */}
      <div className="bg-white rounded-2xl p-6 md:p-8 border border-slate-100 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-50 rounded-xl">
              <Play className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-800">Getting Started</h2>
              <p className="text-sm text-slate-600">Replay the platform introduction</p>
            </div>
          </div>
          <Button
            onClick={() => setShowOnboarding(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6"
          >
            Replay introduction
          </Button>
        </div>
      </div>

      {/* Grow the Community */}
      <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl p-6 md:p-8 border border-teal-200 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-white rounded-xl shadow-sm">
              <Users className="h-6 w-6 text-teal-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-800">Grow the Community</h2>
              <p className="text-sm text-slate-600">Help others on their CLL/SLL journey by inviting them to join</p>
            </div>
          </div>
          <Button
            onClick={() => setShowShareModal(true)}
            className="bg-teal-600 hover:bg-teal-700 text-white px-6 gap-2"
          >
            <Heart className="h-4 w-4" />
            Invite Someone
          </Button>
        </div>
      </div>

      {/* Logout */}
      <div className="bg-white rounded-2xl p-6 md:p-8 border border-slate-100 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-red-50 rounded-xl">
              <LogOut className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-800">Log Out</h2>
              <p className="text-sm text-slate-600">End your current session</p>
            </div>
          </div>
          <Button
            onClick={handleLogout}
            className="bg-red-600 hover:bg-red-700 text-white px-6"
          >
            Log Out
          </Button>
        </div>
      </div>

      {/* Danger Zone - Account Deletion */}
      <div className="bg-white rounded-2xl border border-red-200 shadow-sm overflow-hidden">
        <div className="bg-red-50 px-6 py-4 border-b border-red-200">
          <h2 className="text-lg font-semibold text-red-800">Danger Zone</h2>
        </div>
        <div className="p-6 md:p-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="p-3 bg-red-100 rounded-xl flex-shrink-0">
                <Trash2 className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-800">Close My Account</h3>
                <p className="text-sm text-slate-600 mt-1">
                  Permanently delete your account and all associated data. This includes all your blood results, 
                  symptoms, medications, notes, and any other information you&apos;ve added.
                </p>
                <p className="text-sm text-red-600 mt-2 font-medium">
                  This action cannot be undone.
                </p>
              </div>
            </div>
            <Button
              onClick={openDeleteModal}
              variant="outline"
              className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700 px-6 flex-shrink-0"
            >
              Close my account
            </Button>
          </div>
        </div>
      </div>

      {/* Onboarding Flow (Replay) */}
      {showOnboarding && (
        <OnboardingFlow
          isReplay={true}
          onComplete={() => setShowOnboarding(false)}
          onSkip={() => setShowOnboarding(false)}
        />
      )}

      {/* Country Change Confirmation Dialog */}
      {showCountryDialog && conversionPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 md:p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">Confirm Country Change</h3>
            
            <div className="space-y-4 mb-6">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-sm text-blue-900">
                  <strong>Changing from:</strong> {getCountryName(conversionPreview.from_country)} ({conversionPreview.from_unit})
                  <br />
                  <strong>Changing to:</strong> {getCountryName(selectedCountry)} ({conversionPreview.to_unit})
                </p>
              </div>

              {conversionPreview.needs_conversion ? (
                <>
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <p className="text-sm text-amber-900 font-semibold mb-2">
                      {conversionPreview.affected_count} haemoglobin value(s) will be converted
                    </p>
                    <p className="text-sm text-amber-800">
                      Conversion: {conversionPreview.conversion_explanation}
                    </p>
                  </div>

                  {conversionPreview.examples && conversionPreview.examples.length > 0 && (
                    <div>
                      <p className="text-sm font-semibold text-slate-700 mb-2">Example conversions:</p>
                      <div className="space-y-2">
                        {conversionPreview.examples.map((example, index) => (
                          <div key={index} className="bg-slate-50 rounded-lg p-3 text-sm">
                            <p className="text-slate-600">{example.date}</p>
                            <p className="text-slate-900">
                              {example.old_value} → {example.new_value}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                  <p className="text-sm text-green-900">
                    {conversionPreview.message || 'No haemoglobin values to convert. Your reference ranges will be updated to match the new country.'}
                  </p>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => {
                  setShowCountryDialog(false);
                  setConversionPreview(null);
                }}
                disabled={countryLoading}
                className="flex-1 bg-slate-200 hover:bg-slate-300 text-slate-900"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCountryChangeConfirm}
                disabled={countryLoading}
                className="flex-1 btn-primary"
              >
                {countryLoading ? 'Changing...' : 'Confirm Change'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Account Deletion Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 md:p-8 max-w-lg w-full">
            {/* Modal Header */}
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-red-100 rounded-xl">
                  <Trash2 className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900">Close Your Account</h3>
                  <p className="text-sm text-slate-600 mt-0.5">This action is permanent</p>
                </div>
              </div>
              <button
                onClick={closeDeleteModal}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                aria-label="Close modal"
              >
                <X className="h-5 w-5 text-slate-500" />
              </button>
            </div>

            {/* Warning Content */}
            <div className="space-y-4 mb-6">
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <p className="text-sm text-red-800 leading-relaxed">
                  We understand that sometimes people need to move on. If you choose to close your account, 
                  please know that <strong>all of your data will be permanently deleted</strong>. This includes:
                </p>
                <ul className="mt-3 space-y-1 text-sm text-red-700">
                  <li>• All blood test results and trends</li>
                  <li>• Symptoms, medications, and vitamins logs</li>
                  <li>• Notes and personal reflections</li>
                  <li>• Any other data you&apos;ve added</li>
                </ul>
              </div>

              <p className="text-sm text-slate-600 leading-relaxed">
                Once deleted, there is no way to recover your data. We don&apos;t keep backups of deleted accounts.
              </p>
            </div>

            {/* Confirmation Input */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                To confirm, please type <span className="font-mono text-red-600 bg-red-50 px-2 py-0.5 rounded">{CONFIRM_PHRASE}</span> below:
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Type confirmation phrase"
                className="w-full h-12 px-4 rounded-xl border border-slate-200 bg-white text-lg focus:ring-2 focus:ring-red-200 focus:border-red-400 transition-all"
                autoComplete="off"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                onClick={closeDeleteModal}
                disabled={deleteLoading}
                className="flex-1 bg-slate-200 hover:bg-slate-300 text-slate-900"
              >
                Keep my account
              </Button>
              <Button
                onClick={handleDeleteAccount}
                disabled={deleteLoading || deleteConfirmText !== CONFIRM_PHRASE}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleteLoading ? 'Deleting...' : 'Permanently delete'}
              </Button>
            </div>

            {/* Final Note */}
            <p className="text-xs text-slate-500 text-center mt-4">
              Thank you for using Blood Cancer Companion. We wish you well on your journey.
            </p>
          </div>
        </div>
      )}

      {/* Share MyCBC Modal */}
      <ShareMyCBCModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        showDismissOption={false}
      />
    </div>
  );
};

export default ProfilePage;
